// Pure ECharts gauge option builder for the count/scalar KPI gauge variant (W-A, plan
// DASHBOARD_MISSING_CHART_TYPES.md). Isolated from echartsOptions.js by design — a gauge never routes
// through buildChartOption (it rides the count path), so keeping its option builder here leaves the
// shared chart-option fold contention-free with the coloring session. No echarts import, no DOM, no
// mutation: returns a fresh plain option object consumed by EChartsWidget.vue.
import { CHART_TEXT } from './chartTheme';

// Tone → arc-fill hex pair, BY VALUE (mirrors echartsOptions.js TONE_FILL and the KPI tone colours) so
// this module stays decoupled from the coloring fold, per the plan's "reuse severity constants by
// value, not import". Gauge band colours resolve through this.
const TONE_HEX = Object.freeze({
  good: { light: '#03624c', dark: '#2cc295' },
  caution: { light: '#a07a00', dark: '#d2a000' },
  critical: { light: '#b71c1c', dark: '#ef5350' },
});

// Neutral fallback arc colour when a band carries no resolvable paint (or there are no bands).
const NEUTRAL_ARC = Object.freeze({ light: '#c7cfcb', dark: 'rgba(255, 255, 255, 0.24)' });

function pickTheme(pair, theme) {
  return theme === 'dark' ? pair.dark : pair.light;
}

// One band's concrete hex for the current theme: a semantic tone, else a raw {light,dark} colour
// pair, else the neutral fallback.
function bandHex(band, theme) {
  if (band && band.tone && TONE_HEX[band.tone]) return pickTheme(TONE_HEX[band.tone], theme);
  if (band && band.color && (band.color.light || band.color.dark)) {
    return pickTheme({ light: band.color.light || band.color.dark, dark: band.color.dark || band.color.light }, theme);
  }
  return pickTheme(NEUTRAL_ARC, theme);
}

// Coerce to a finite number, or null.
function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// axisLine colour stops [[fraction, hex], …] from the ordered half-open bands. Each band's fraction
// is its upper boundary mapped into [0,1] across [min,max]; the last band always closes the arc at 1.
// Fractions are clamped monotonic non-decreasing within [0,1] so a missing/out-of-order boundary can
// never produce a backwards or >1 stop (echarts would drop the whole axisLine).
function bandStops(bands, min, max, theme) {
  const span = max - min;
  const list = Array.isArray(bands) ? bands : [];
  if (!list.length || span <= 0) {
    return [[1, pickTheme(NEUTRAL_ARC, theme)]];
  }
  const stops = [];
  let prevFrac = 0;
  for (let i = 0; i < list.length; i++) {
    const isLast = i === list.length - 1;
    let frac;
    if (isLast) {
      frac = 1;
    } else {
      const to = num(list[i].to);
      frac = to === null ? prevFrac : (to - min) / span;
    }
    if (frac < prevFrac) frac = prevFrac;
    if (frac > 1) frac = 1;
    stops.push([frac, bandHex(list[i], theme)]);
    prevFrac = frac;
  }
  // Guarantee the final stop closes the arc even if the last band's boundary resolved short.
  if (stops.length && stops[stops.length - 1][0] < 1) stops[stops.length - 1][0] = 1;
  return stops;
}

// Value → display string: integers as-is, otherwise up to 2 decimals with trailing precision trimmed.
function formatValue(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v == null ? '' : v);
  if (Number.isInteger(n)) return String(n);
  return String(Math.round(n * 100) / 100);
}

/**
 * Build an ECharts gauge option for a scalar KPI (the count-path gauge variant). Pure.
 *
 * @param {number} value the scalar to plot (dashboard-widget.count)
 * @param {{ min?: number, max?: number, bands?: Array<{ to?: number, tone?: string, color?: object, label?: string }> }} gaugeCfg
 *        static Phase-1 gauge config; bands are the SchemaBandsEditor shape (last band = "up to max")
 * @param {{ theme?: 'light'|'dark', unit?: string, label?: string, scale?: number }} [ctx]
 *        label = the widget's metric label ("Average of Remaining Life"), shown under the value;
 *        scale = the Widget-size px multiplier for the dial text (small 0.85 / normal 1 / large 1.2)
 * @returns {Object} a fresh ECharts option ({ series: [{ type: 'gauge' }] })
 */
export function buildGaugeOption(value, gaugeCfg, ctx) {
  const theme = ctx && ctx.theme === 'dark' ? 'dark' : 'light';
  // Widget-size px scale. Absent or invalid -> 1, so the dial's hardcoded 9/11/26px are emitted
  // verbatim (byte-identical to pre-feature output).
  const scale = ctx && Number.isFinite(ctx.scale) && ctx.scale > 0 ? ctx.scale : 1;
  const cfg = gaugeCfg || {};
  // Defensive range: never emit a zero or inverted span (echarts divides the axis by it).
  let min = num(cfg.min);
  let max = num(cfg.max);
  if (min === null) min = 0;
  if (max === null || max <= min) max = min + 100;

  const unit = ctx && typeof ctx.unit === 'string' ? ctx.unit.trim() : '';
  const label = ctx && typeof ctx.label === 'string' ? ctx.label.trim() : '';
  const stops = bandStops(cfg.bands, min, max, theme);
  const val = num(value);
  const shown = val == null ? min : val;

  return {
    series: [{
      type: 'gauge',
      min,
      max,
      startAngle: 210,
      endAngle: -30,
      splitNumber: 4,
      radius: '92%',
      center: ['50%', '56%'],
      axisLine: { lineStyle: { width: 14, color: stops } },
      progress: { show: false },
      pointer: { width: 4, length: '62%', itemStyle: { color: 'auto' } },
      anchor: { show: true, size: 8, itemStyle: { color: 'auto' } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { distance: 14, color: CHART_TEXT.MUTED, fontSize: Math.round(9 * scale) },
      // Metric label under the value (e.g. "Average of Remaining Life"), truncated to fit the dial.
      title: {
        show: !!label,
        offsetCenter: [0, '56%'],
        color: CHART_TEXT.MUTED,
        fontSize: Math.round(11 * scale),
        fontFamily: CHART_TEXT.FONT_FAMILY,
        overflow: 'truncate',
        width: 150,
      },
      detail: {
        valueAnimation: false,
        offsetCenter: [0, '30%'],
        fontSize: Math.round(26 * scale),
        fontWeight: 600,
        fontFamily: CHART_TEXT.FONT_FAMILY,
        color: CHART_TEXT.STRONG,
        formatter: v => `${formatValue(v)}${unit ? ` ${unit}` : ''}`,
      },
      data: [{ value: shown, name: label }],
    }],
  };
}
